import { useEffect, useState } from "react";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const MONTHS_ES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];

export const OccupancyCalendar = () => {
  const [cursor, setCursor] = useState(() => new Date());
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/admin/calendar?month=${monthKey(cursor)}`).then((r) => setData(r.data)).catch(() => {});
  }, [cursor]);

  const move = (delta) => {
    const d = new Date(cursor);
    d.setMonth(d.getMonth() + delta);
    setCursor(d);
  };

  if (!data) return <p className="text-[#A0A3A9] text-sm p-6">Cargando calendario…</p>;

  const days = Array.from({ length: data.days }, (_, i) => i + 1);
  const [y, m] = data.month.split("-").map(Number);
  const today = new Date();
  const isCurrentMonth = today.getFullYear() === y && today.getMonth() + 1 === m;

  const spans = (slug) =>
    data.bookings
      .filter((b) => b.apartment_slug === slug)
      .map((b) => {
        const ci = new Date(`${b.checkin}T00:00:00`);
        const co = new Date(`${b.checkout}T00:00:00`);
        const monthStart = new Date(y, m - 1, 1);
        const monthEnd = new Date(y, m - 1, data.days);
        const lastNight = new Date(co);
        lastNight.setDate(lastNight.getDate() - 1);
        const start = ci < monthStart ? 1 : ci.getDate();
        const end = lastNight > monthEnd ? data.days : lastNight.getDate();
        if (end < 1 || start > data.days || end < start) return null;
        return { ...b, start, end };
      })
      .filter(Boolean);

  return (
    <div data-testid="occupancy-calendar" className="bg-[#181A1F] border border-[#2A2D35]">
      <div className="flex items-center justify-between px-5 py-4 border-b border-[#2A2D35]">
        <h2 className="text-[#FDFBF7] font-serif text-2xl">{MONTHS_ES[m - 1]} {y}</h2>
        <div className="flex gap-2">
          <button data-testid="cal-prev" onClick={() => move(-1)} className="p-2 border border-[#2A2D35] text-[#A0A3A9] hover:text-[#FDFBF7] hover:border-[#D5C0A1] transition-colors"><CaretLeft size={16} /></button>
          <button data-testid="cal-next" onClick={() => move(1)} className="p-2 border border-[#2A2D35] text-[#A0A3A9] hover:text-[#FDFBF7] hover:border-[#D5C0A1] transition-colors"><CaretRight size={16} /></button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <div className="min-w-[900px]">
          <div className="flex border-b border-[#2A2D35]">
            <div className="w-44 shrink-0 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-[#A0A3A9]">Apartamento</div>
            <div className="flex-1 flex">
              {days.map((d) => (
                <div key={d} className={`flex-1 text-center text-[10px] py-2 ${isCurrentMonth && d === today.getDate() ? "text-[#D5C0A1] font-semibold" : "text-[#A0A3A9]/70"}`}>
                  {d}
                </div>
              ))}
            </div>
          </div>

          {data.apartments.map((apt) => (
            <div key={apt.slug} data-testid={`cal-row-${apt.slug}`} className="flex border-b border-[#2A2D35] last:border-b-0">
              <div className="w-44 shrink-0 px-4 py-0 flex items-center gap-2.5 border-r border-[#2A2D35]">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: apt.color }} />
                <span className="text-sm text-[#FDFBF7] truncate">{apt.name}</span>
              </div>
              <div className="flex-1 relative h-12">
                <div className="absolute inset-0 flex">
                  {days.map((d) => (
                    <div key={d} className={`flex-1 border-r border-[#2A2D35]/40 ${isCurrentMonth && d === today.getDate() ? "bg-[#D5C0A1]/5" : ""}`} />
                  ))}
                </div>
                {spans(apt.slug).map((b) => (
                  <div
                    key={b.id}
                    data-testid={`cal-booking-${b.id.slice(0, 8)}`}
                    title={`${b.guest_name} · ${b.checkin} → ${b.checkout} · ${b.status === "confirmed" ? "Confirmada" : "Pendiente de pago"}`}
                    className={`absolute top-1.5 bottom-1.5 flex items-center px-2 overflow-hidden ${b.status !== "confirmed" ? "opacity-50" : ""}`}
                    style={{
                      left: `${((b.start - 1) / data.days) * 100}%`,
                      width: `${((b.end - b.start + 1) / data.days) * 100}%`,
                      background: apt.color,
                    }}
                  >
                    <span className="text-[10px] text-white/90 truncate tracking-wide">{b.guest_name}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div className="flex flex-wrap gap-5 px-5 py-4 border-t border-[#2A2D35]">
            {data.apartments.map((apt) => (
              <span key={apt.slug} className="flex items-center gap-2 text-[10px] uppercase tracking-[0.15em] text-[#A0A3A9]">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: apt.color }} />
                {apt.name}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
